import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "../components/UserMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ArrowLeft, Users, Plus, Pencil, Trash2, Search } from "lucide-react";
import { useStickyState } from "../safety/listPrefs";
import ResetFiltersChip from "../safety/ResetFiltersChip";
import SafetyShell from "../safety/SafetyShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const customer_base_label_js = (s) => {
  if (!s) return "";
  const base = s.split(",")[0];
  return base.replace(/^\s*M\/[Ss]\.?\s*/, "").replace(/[.\s]+$/, "").trim().toUpperCase();
};

export default function CustomersMaster({ user, onLogout }) {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useStickyState("safety:customers:search", "");
  const [dialog, setDialog] = useState({ open: false, mode: "create", customer: null });
  const [form, setForm] = useState({ name: "", aliases: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, customer: null });
  const [tab, setTab] = useState("customers"); // customers | grouping
  const [grouping, setGrouping] = useState([]);
  const [groupingLoading, setGroupingLoading] = useState(false);
  const [onlyUnassigned, setOnlyUnassigned] = useState(true);
  const [assignSel, setAssignSel] = useState({}); // raw_name -> customer_id | "__new__"
  const [assignBusy, setAssignBusy] = useState("");
  const [selectedRaws, setSelectedRaws] = useState([]);
  const [bulkCustomer, setBulkCustomer] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [verticals, setVerticals] = useState([]);
  const [newVertical, setNewVertical] = useState("");

  useEffect(() => { loadGrouping(); /* for the unassigned badge */ /* eslint-disable-next-line */ }, []);

  const unassignedCount = grouping.filter((g) => !g.mapped).length;

  const toggleRaw = (raw) => setSelectedRaws((s) => s.includes(raw) ? s.filter((x) => x !== raw) : [...s, raw]);

  const bulkAssign = async () => {
    if (!bulkCustomer || selectedRaws.length === 0) return;
    setBulkBusy(true);
    try {
      for (const raw of selectedRaws) {
        await axios.post(`${API}/customers/${bulkCustomer}/aliases`, { alias: raw }, auth());
      }
      toast.success(`Grouped ${selectedRaws.length} name(s)`);
      setSelectedRaws([]); setBulkCustomer("");
      await Promise.all([load(), loadGrouping()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk assign failed");
    } finally { setBulkBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);
  useEffect(() => { if (tab === "grouping") loadGrouping(); /* eslint-disable-next-line */ }, [tab]);

  const loadGrouping = async () => {
    setGroupingLoading(true);
    try {
      const res = await axios.get(`${API}/customers/grouping`, auth());
      setGrouping(res.data?.groups || []);
    } catch (e) { toast.error("Failed to load customer grouping"); }
    finally { setGroupingLoading(false); }
  };

  const assignRaw = async (g) => {
    const sel = assignSel[g.raw_name];
    if (!sel) { toast.error("Pick a customer"); return; }
    setAssignBusy(g.raw_name);
    try {
      if (sel === "__new__") {
        await axios.post(`${API}/customers`, { name: (customer_base_label_js(g.raw_name) || g.raw_name), aliases: [g.raw_name] }, auth());
      } else {
        await axios.post(`${API}/customers/${sel}/aliases`, { alias: g.raw_name }, auth());
      }
      toast.success("Grouped");
      setAssignSel((s) => ({ ...s, [g.raw_name]: undefined }));
      await Promise.all([load(), loadGrouping()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to group");
    } finally { setAssignBusy(""); }
  };

  const load = async () => {
    try {
      const permRes = await axios.get(`${API}/auth/me/permissions`, auth());
      setPermissions(permRes.data.effective_permissions || []);
      const url = search ? `${API}/customers?search=${encodeURIComponent(search)}` : `${API}/customers`;
      const res = await axios.get(url, auth());
      setCustomers(res.data);
      try {
        const vres = await axios.get(`${API}/verticals`, auth());
        setVerticals(vres.data.items || []);
      } catch { /* verticals optional */ }
    } catch (e) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const assignVertical = async (customerId, verticalId) => {
    try {
      await axios.put(`${API}/customers/${customerId}`, { vertical_id: verticalId || "" }, auth());
      setCustomers((cs) => cs.map((c) => c.id === customerId ? { ...c, vertical_id: verticalId || null } : c));
    } catch { toast.error("Failed to set vertical"); }
  };
  const createVertical = async () => {
    const name = newVertical.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/verticals`, { name }, auth());
      setNewVertical("");
      const vres = await axios.get(`${API}/verticals`, auth());
      setVerticals(vres.data.items || []);
      toast.success(`Vertical "${name}" added`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add vertical"); }
  };
  const deleteVertical = async (id) => {
    try {
      await axios.delete(`${API}/verticals/${id}`, auth());
      const vres = await axios.get(`${API}/verticals`, auth());
      setVerticals(vres.data.items || []);
      load();
      toast.success("Vertical removed");
    } catch { toast.error("Failed to remove vertical"); }
  };

  const canManage = permissions.includes("customer.manage");

  const openCreate = () => {
    setForm({ name: "", aliases: "", notes: "" });
    setDialog({ open: true, mode: "create", customer: null });
  };
  const openEdit = (c) => {
    setForm({ name: c.name || "", aliases: (c.aliases || []).join(", "), notes: c.notes || "" });
    setDialog({ open: true, mode: "edit", customer: c });
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Customer name is required"); return; }
    setSaving(true);
    try {
      const body = {
        name,
        aliases: form.aliases.split(",").map(s => s.trim()).filter(Boolean),
        notes: form.notes.trim() || null,
      };
      if (dialog.mode === "create") {
        await axios.post(`${API}/customers`, body, auth());
        toast.success("Customer created");
      } else {
        await axios.put(`${API}/customers/${dialog.customer.id}`, body, auth());
        toast.success("Customer updated");
      }
      setDialog({ open: false, mode: "create", customer: null });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await axios.delete(`${API}/customers/${deleteDialog.customer.id}`, auth());
      toast.success("Customer deleted");
      setDeleteDialog({ open: false, customer: null });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
    </div>;
  }

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Customers" }]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Customers", active: true, depth: 1 },
        { label: "Purchase Orders", to: "/safety/po", depth: 1 },
      ]}
    >
      <div data-testid="customers-master-page">
      <div className="sf-surface p-4 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-orange-600" />
          <div>
            <h1 className="text-xl font-bold tracking-tight">Customers Master</h1>
            <p className="text-xs text-zinc-500">Manage customers who send POs</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-4 border-b">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "customers" ? "border-orange-600 text-orange-700" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}
          onClick={() => setTab("customers")}
          data-testid="customers-tab-master"
        >Customers</button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 ${tab === "grouping" ? "border-orange-600 text-orange-700" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}
          onClick={() => setTab("grouping")}
          data-testid="customers-tab-grouping"
        >
          Customer grouping
          {unassignedCount > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold" data-testid="grouping-unassigned-badge">{unassignedCount}</span>}
        </button>
      </div>

      {tab === "customers" && (
      <div>
        <div className="flex items-center justify-between mb-4 gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <Input
              placeholder="Search by name or alias"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="customers-search-input"
            />
          </div>
          <ResetFiltersChip active={search !== ""} onReset={() => setSearch("")} />
          {canManage && (
            <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-customer-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Customer
            </Button>
          )}
        </div>

        <div className="bg-white rounded-lg border shadow-sm mb-4 p-3" data-testid="verticals-manager">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-zinc-600 mr-1">Verticals:</span>
            {verticals.length === 0 && <span className="text-xs text-zinc-400">None yet — add one to group customers for the Management Report.</span>}
            {verticals.map((v) => (
              <span key={v.id} className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full border" style={{ borderColor: v.color, color: v.color }} data-testid={`vertical-chip-${v.id}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: v.color }} />
                {v.name}
                <span className="text-zinc-400">· {v.customer_count}</span>
                {canManage && (
                  <button onClick={() => deleteVertical(v.id)} className="text-zinc-300 hover:text-red-500" title="Remove vertical" data-testid={`vertical-delete-${v.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
            {canManage && (
              <div className="inline-flex items-center gap-1 ml-1">
                <Input value={newVertical} onChange={(e) => setNewVertical(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createVertical()} placeholder="New vertical name" className="h-8 w-44 text-xs" data-testid="vertical-new-input" />
                <Button size="sm" variant="outline" className="h-8" onClick={createVertical} data-testid="vertical-add-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Vertical</th>
                <th className="p-3 text-left">Aliases</th>
                <th className="p-3 text-left">Notes</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 && (
                <tr><td colSpan="5" className="p-6 text-center text-gray-500" data-testid="no-customers">No customers yet. Click "Add Customer" to create one.</td></tr>
              )}
              {customers.map(c => (
                <tr key={c.id} className="border-b hover:bg-gray-50" data-testid={`customer-row-${c.id}`}>
                  <td className="p-3 font-medium">{c.name}</td>
                  <td className="p-3">
                    {canManage ? (
                      <select
                        value={c.vertical_id || ""}
                        onChange={(e) => assignVertical(c.id, e.target.value)}
                        className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-xs max-w-[180px]"
                        data-testid={`customer-vertical-select-${c.id}`}
                      >
                        <option value="">— Unassigned —</option>
                        {verticals.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                      </select>
                    ) : (
                      (() => { const v = verticals.find((x) => x.id === c.vertical_id); return v ? <span className="text-xs font-medium" style={{ color: v.color }}>{v.name}</span> : <span className="text-xs text-zinc-400">—</span>; })()
                    )}
                  </td>
                  <td className="p-3 text-gray-600">{(c.aliases || []).join(", ") || "-"}</td>
                  <td className="p-3 text-gray-600 max-w-md truncate">{c.notes || "-"}</td>
                  <td className="p-3">
                    {canManage && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}>
                          <Pencil className="w-3 h-3 mr-1" />Edit
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => setDeleteDialog({ open: true, customer: c })} data-testid={`delete-customer-${c.id}`}>
                          <Trash2 className="w-3 h-3 mr-1" />Delete
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {tab === "grouping" && (
      <div data-testid="customer-grouping-panel">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <p className="text-xs text-zinc-500 max-w-2xl">Every consignee/customer name found on packing lists and how it currently groups. Assign location variants (e.g. "…, NEW YORK", "…, CHICAGO") to one master customer so filters show a single entry.</p>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-zinc-600">
              <input type="checkbox" checked={onlyUnassigned} onChange={(e) => setOnlyUnassigned(e.target.checked)} data-testid="grouping-only-unassigned" />
              Only unassigned
            </label>
            <Button size="sm" variant="outline" onClick={loadGrouping} data-testid="grouping-refresh">Refresh</Button>
          </div>
        </div>
        {canManage && selectedRaws.length > 0 && (
          <div className="flex items-center gap-2 mb-3 bg-orange-50 border border-orange-200 rounded-md px-3 py-2" data-testid="grouping-bulk-bar">
            <span className="text-sm font-medium text-orange-800">{selectedRaws.length} selected</span>
            <span className="text-orange-400">→</span>
            <select value={bulkCustomer} onChange={(e) => setBulkCustomer(e.target.value)} className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-xs max-w-[220px]" data-testid="grouping-bulk-select">
              <option value="">Assign all to…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Button size="sm" disabled={!bulkCustomer || bulkBusy} onClick={bulkAssign} className="bg-orange-600 hover:bg-orange-700" data-testid="grouping-bulk-assign">
              {bulkBusy ? "Assigning…" : "Assign selected"}
            </Button>
            <button className="text-xs text-zinc-500 hover:text-zinc-800 ml-1" onClick={() => setSelectedRaws([])}>Clear</button>
          </div>
        )}
        <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {canManage && <th className="p-3 w-8"></th>}
                <th className="p-3 text-left">Raw name (from files)</th>
                <th className="p-3 text-center">On PLs</th>
                <th className="p-3 text-left">Resolves to</th>
                <th className="p-3 text-center">Status</th>
                {canManage && <th className="p-3 text-left">Assign to customer</th>}
              </tr>
            </thead>
            <tbody>
              {groupingLoading && <tr><td colSpan={canManage ? 6 : 4} className="p-6 text-center text-gray-400">Loading…</td></tr>}
              {!groupingLoading && (() => {
                const rows = grouping.filter((g) => !onlyUnassigned || !g.mapped);
                if (rows.length === 0) return <tr><td colSpan={canManage ? 6 : 4} className="p-6 text-center text-gray-500" data-testid="grouping-empty">{onlyUnassigned ? "All customer names are assigned to a master customer. 🎉" : "No customer names found on packing lists yet."}</td></tr>;
                return rows.map((g) => (
                  <tr key={g.raw_name} className="border-b hover:bg-gray-50" data-testid={`grouping-row-${g.raw_name}`}>
                    {canManage && (
                      <td className="p-3 text-center">
                        <input type="checkbox" checked={selectedRaws.includes(g.raw_name)} onChange={() => toggleRaw(g.raw_name)} data-testid={`grouping-check-${g.raw_name}`} />
                      </td>
                    )}
                    <td className="p-3 font-mono text-xs max-w-[280px] truncate" title={g.raw_name}>{g.raw_name}</td>
                    <td className="p-3 text-center text-gray-500">{g.count}</td>
                    <td className="p-3 font-medium">{g.canonical}</td>
                    <td className="p-3 text-center">
                      {g.mapped ? <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">Mapped</span>
                        : g.auto_grouped ? <span className="inline-block px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold">Auto-grouped</span>
                        : <span className="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">Ungrouped</span>}
                    </td>
                    {canManage && (
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <select
                            value={assignSel[g.raw_name] || ""}
                            onChange={(e) => setAssignSel((s) => ({ ...s, [g.raw_name]: e.target.value }))}
                            className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-xs max-w-[180px]"
                            data-testid={`grouping-select-${g.raw_name}`}
                          >
                            <option value="">— Select —</option>
                            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            <option value="__new__">➕ New: {customer_base_label_js(g.raw_name)}</option>
                          </select>
                          <Button size="sm" variant="outline" disabled={!assignSel[g.raw_name] || assignBusy === g.raw_name} onClick={() => assignRaw(g)} data-testid={`grouping-assign-${g.raw_name}`}>
                            {assignBusy === g.raw_name ? "…" : "Assign"}
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, mode: "create", customer: null })}>
        <DialogContent data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.mode === "create" ? "Add Customer" : "Edit Customer"}</DialogTitle>
            <DialogDescription>Customers are companies that issue Purchase Orders to you.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Handle It" data-testid="customer-name-input" />
            </div>
            <div>
              <Label>Aliases (comma-separated)</Label>
              <Input value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} placeholder="HandleIt, Handle-It Pvt Ltd" data-testid="customer-aliases-input" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" data-testid="customer-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, mode: "create", customer: null })}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-customer-btn">
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, customer: null })}>
        <DialogContent data-testid="delete-customer-dialog">
          <DialogHeader>
            <DialogTitle>Delete customer?</DialogTitle>
            <DialogDescription>
              This will permanently remove <b>{deleteDialog.customer?.name}</b>. The customer cannot be deleted if referenced by any PO.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, customer: null })}>Cancel</Button>
            <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-customer-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </SafetyShell>
  );
}
